//angular imports
import {
  Component,
  OnInit,
  Output,
  EventEmitter,
  OnDestroy,
  Signal,
  computed,
  signal,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';

//msal imports
import { MsalService } from '@azure/msal-angular';
import { AccountInfo } from '@azure/msal-browser';

//local imports
import { UserPermissionsService } from '../../_services/user-permissions.service';
import { PageReloadService } from '../../_services/page-reload-service';
import { UserNotificationService } from 'src/app/_services/notification.service';
import { GroupByService } from '../../_services/groupBy.service';
import { LibraryNavigationService } from '../../_services/libraryNavigationService';
import { expandCamelCase } from '../../_helpers/camelCaseHelper';
import { ReferenceType } from '../../_enums/referenceType';
import { AppConfig } from 'src/app/app-config';
import { navigationIcons } from '../../_models/navigationIcons';
import { Persona, formatPersona } from '../../_enums/persona';
import { PersonaService } from '../../_services/persona.service';
import {
  CheckedFilters,
  FilterContext,
  FiltersPaneComponent,
  FiltersPaneMode
} from '../../filters/';

@Component({
  selector: 'app-navigation',
  templateUrl: './navigation.component.html',
  styleUrls: ['./navigation.component.css']
})

export class NavigationComponent implements OnInit, OnDestroy {
  public formatPersona = formatPersona;
  currentUser: AccountInfo;
  isDevelopmentStage: boolean = false;

  submitted = false;
  loading = false;

  popupWindowTitle: string;
  popupWindowOpen = false;

  @Output() IsnavbarOpen = new EventEmitter<boolean>();
  navbarOpen = true;

  reportsSubMenu: NavigationSubMenu[] = [];
  librarySubMenu = signal<NavigationSubMenu[]>([]);
  catalogSubMenu = signal<NavigationSubMenu[]>([]);

  inLibrary: boolean = false;
  inCatalog: boolean = false;
  groupBySubsciption: any;
  private personaServiceInitializedSubscription: Subscription;
  private personaChangedSubscription: Subscription;

  FiltersPaneMode = FiltersPaneMode;

  @ViewChild('filtersPane') filtersPane: FiltersPaneComponent;

  currentPanel: WritableSignal<FilterContext> = signal(FilterContext.Company);

  constructor(
    private authenticationService: MsalService,
    private notificationService: UserNotificationService,
    private router: Router,
    private groupByService: GroupByService,
    private pageReloadService: PageReloadService,
    public libraryNavigationService: LibraryNavigationService,
    private appConfig: AppConfig,
    private dialog: MatDialog,
    public userPermissionsService: UserPermissionsService,
    public personaService: PersonaService,
    private httpClient: HttpClient,
  ) {    
    this.personaServiceInitializedSubscription = this.personaService.initialized().subscribe(loaded => {
      if (loaded) {
        this.groupBySubsciption = this.groupByService.groupBysUpdated.subscribe(_ => {
          this.loadLibrarySubMenu();
          this.loadCatalogSubMenu();
        });
        this.personaChangedSubscription = this.personaService.changed().subscribe(_ => {
          if (this.personaService.currentPersona() != null) {
            this.loadLibrarySubMenu();
            this.loadCatalogSubMenu();
          }
        });
      }
    });

    this.inLibrary = this.router.url.indexOf('home/documents') > -1;
    this.inCatalog = this.router.url.indexOf('home/catalog') > -1;
  }

  public panelOpened(context: FilterContext) {
    this.currentPanel.set(context);
  }

  public personaSelected(persona: Persona) {
    this.personaService.setPersona(persona);
    var location = this.inLibrary ? 'documents' : (this.inCatalog ? 'catalog' : '');
    if (location != '') {
      let url = `/home/${location}/view/home`;
      this.router.navigateByUrl(url);
  }

  }

  public loadLibrarySubMenu() {
    if (this.router.url.indexOf('home/documents') > -1) {
      let newNavigationSubMenu: NavigationSubMenu[] = [];

      newNavigationSubMenu.push(new NavigationSubMenu('Recent', 'recent', 'clock', false));
      newNavigationSubMenu.push(new NavigationSubMenu('Starred', 'starred', 'star', false));
      newNavigationSubMenu.push(new NavigationSubMenu('Downloads', 'downloads/', 'file-arrow-down', false));
      if (this.personaService.currentPersona() != null) {
        this.groupByService
          .getTopLevelGroupBysForPersonaInLibrary(this.personaService.currentPersona())
          .subscribe({
            next: groupBys => {
              let contexts: FilterContext[] = [FilterContext.Company, FilterContext.MasterCatalog, FilterContext.ProgramCatalog, FilterContext.ProjectCatalog];
              contexts.forEach((context: FilterContext) => {
                let contextMenuItems: NavigationSubMenu[] = [];
                groupBys.forEach((groupBy) => {
                  if (groupBy.context == context)
                    contextMenuItems.push(new NavigationSubMenu(
                      expandCamelCase(ReferenceType[groupBy.referenceType]),
                      'wayfinder/' + groupBy.context + '.' + groupBy.referenceType,
                      navigationIcons[groupBy.referenceType], false));
                });
                if (contextMenuItems.length > 0) {
                  newNavigationSubMenu.push(new NavigationSubMenu(expandCamelCase(FilterContext[context]), '', '', true));
                  newNavigationSubMenu = newNavigationSubMenu.concat(contextMenuItems);
                }
              });



              let change = false;

              if (this.librarySubMenu().length != newNavigationSubMenu.length) {
                change = true;
              }
              else {
                for (let i = 0; i < this.librarySubMenu().length; i++) {
                  if (this.librarySubMenu()[i].Name !== newNavigationSubMenu[i].Name) {
                    change = true;
                    break;
                  }
                }
              }

              if (change) this.librarySubMenu.set(newNavigationSubMenu);
            },
            error: error => {
              this.notificationService.showNotification(`An unexpected error occurrred. ${error?.error ? error.error : ''}`, 'error');
            }
          });
      }
    } else this.librarySubMenu.set([]);
  }

  public loadCatalogSubMenu() {
    if (this.router.url.indexOf('home/catalog') > -1) {
      let newNavigationSubMenu: NavigationSubMenu[] = [];
      let reports: NavigationSubMenu = new NavigationSubMenu('Reports', 'reports', 'file-contract', false);
      newNavigationSubMenu.push(reports);

      this.groupByService
        .getTopLevelGroupBysForPersonaInCatalog(this.personaService.currentPersona())
        .subscribe({
            next: groupBys => {
              let contexts: FilterContext[] = [FilterContext.Company, FilterContext.MasterCatalog, FilterContext.ProgramCatalog, FilterContext.ProjectCatalog];
              contexts.forEach((context: FilterContext) => {
                let contextMenuItems: NavigationSubMenu[] = [];
                groupBys.forEach((groupBy) => {
                  if (groupBy.context == context)
                    contextMenuItems.push(new NavigationSubMenu(
                      expandCamelCase(ReferenceType[groupBy.referenceType]),
                      'view/wayfinder/' + groupBy.context + '.' + groupBy.referenceType,
                      navigationIcons[groupBy.referenceType], false));
                });
                if (contextMenuItems.length > 0) {
                  newNavigationSubMenu.push(new NavigationSubMenu(expandCamelCase(FilterContext[context]), '', '', true));
                  newNavigationSubMenu = newNavigationSubMenu.concat(contextMenuItems);
              }
              });

              let change: boolean = false;
              if (this.catalogSubMenu().length > 0 && this.catalogSubMenu()[0].children.length != reports.children.length) change = true;
              if (this.catalogSubMenu().length != newNavigationSubMenu.length) change = true;
              else for (let i = 0; i < this.catalogSubMenu().length; i++) if (this.catalogSubMenu()[i].Name !== newNavigationSubMenu[i].Name) change = true;
              if (change) this.catalogSubMenu.set(newNavigationSubMenu);            },
            error: error => {
              this.notificationService.showNotification(`An unexpected error occurrred. ${error?.error ? error.error : ''}`, 'error');
            }
          });
    } else this.catalogSubMenu.set([]);
  }

  onPageReload() {
    this.loadLibrarySubMenu();
    this.loadCatalogSubMenu();
    this.inLibrary = this.router.url.indexOf('home/documents') > -1;
    this.inCatalog = this.router.url.indexOf('home/catalog') > -1;
  }

  ngOnInit() {
    if (this.appConfig.stage == "dev" || this.appConfig.stage == "testing") {
      this.isDevelopmentStage = true;
    }
    this.pageReloadService.subscribeReloadMethod(this);
    this.currentUser = this.authenticationService.instance.getActiveAccount();   
  }

  logout() {
    this.authenticationService.logoutRedirect();
  }

  toggleNavbar() {
    this.navbarOpen = !this.navbarOpen;
    this.IsnavbarOpen.emit(this.navbarOpen);
  }
  

  ngOnDestroy() {
    this.pageReloadService.unsubscribeReloadMethod();
    this.personaServiceInitializedSubscription.unsubscribe();
    this.personaChangedSubscription.unsubscribe();
  }

  filterChecked(): void {
    let filters: CheckedFilters = this.filtersPane.getCheckedFilters();
    this.libraryNavigationService.filtersStateChanged(filters);
  }
}

class NavigationSubMenu {
  children: NavigationSubMenu[] = [];
  constructor(
    public Name: string,
    public Route: string,
    public IconCode: string,
    public IsHeading: boolean
  ) { }
}
